import React, { useState, useEffect, useCallback, useRef } from 'react';
import useConfirm from '../Components/Modals/useConfirm';
import { useNavigate } from 'react-router-dom';
import useSoloStore, { BOT_CONFIGS } from '../stores/soloStore';
import { PeekingMascot, BotMascot, roundMood } from '../Components/Mascot';
import Kbd from '../Components/Kbd';
import { finePointer } from '../utils/pointer';

const TOTAL_SECONDS = 30;
// Seconds into the round when each bot "submits". The answers exist from the
// start; this is when the player is told, so Easy really does leave time.
const BOT_SUBMIT_AT = { kafi: 22, kosi: 15, kemi: 9 };
// The round's end time is saved so a refresh can't restart the clock.
const DEADLINE_KEY = 'nameit.solo.roundDeadline';

const readDeadline = (roundKey) => {
  try {
    const saved = JSON.parse(localStorage.getItem(DEADLINE_KEY) || 'null');
    return saved?.roundKey === roundKey ? saved.endsAt : null;
  } catch {
    return null;
  }
};

const startRoundClock = (roundKey) => {
  // A deadline long past is from an abandoned game that happened to share
  // this round number and letter, not a refresh of this one.
  const existing = readDeadline(roundKey);
  if (existing && existing > Date.now() - 5000) return existing;
  const endsAt = Date.now() + TOTAL_SECONDS * 1000;
  try {
    localStorage.setItem(DEADLINE_KEY, JSON.stringify({ roundKey, endsAt }));
  } catch {
    /* storage unavailable: the clock just won't survive a refresh */
  }
  return endsAt;
};

const secondsUntil = (endsAt) => Math.max(0, Math.ceil((endsAt - Date.now()) / 1000));

const ALL_LETTERS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('');

const CATEGORY_LABELS = {
  name: 'Name',
  animal: 'Animal',
  place: 'Place',
  thing: 'Thing',
};

export default function SoloPlayPhase() {
  const [confirmDialog, ask] = useConfirm();
  const navigate = useNavigate();
  const {
    currentLetter,
    selectedBot,
    categories,
    botAnswers,
    playedLetters,
    currentRound,
    totalRounds,
    gameMode,
    userScore,
    botScore,
    setUserAnswers,
    endGame,
    resetGame,
  } = useSoloStore();

  async function handleQuit() {
    const ok = await ask({
      title: 'Quit this game?',
      message: 'The scores so far are saved to your record.',
      confirmLabel: 'Quit game',
      cancelLabel: 'Keep playing',
    });
    if (!ok) return;
    const result = userScore > botScore ? 'win' : userScore < botScore ? 'loss' : 'tie';
    endGame(result);
    useSoloStore.setState({ gamePhase: 'finished' });
  }

  const [inputs, setInputs] = useState(
    Object.fromEntries(categories.map((c) => [c, '']))
  );
  const roundKey = `${currentRound}-${currentLetter}`;
  const [endsAt] = useState(() => startRoundClock(roundKey));
  const [timeLeft, setTimeLeft] = useState(() => secondsUntil(endsAt));
  const [submitted, setSubmitted] = useState(false);
  const botName = BOT_CONFIGS[selectedBot]?.name;
  const hasBotAnswers = Object.values(botAnswers).some((v) => v);
  const botSubmitAt = BOT_SUBMIT_AT[selectedBot] ?? 15;
  const botReady = hasBotAnswers && TOTAL_SECONDS - timeLeft >= botSubmitAt;

  useEffect(() => {
    if (submitted) return;
    if (timeLeft <= 0) { handleSubmit(true); return; }
    const id = setTimeout(() => setTimeLeft(secondsUntil(endsAt)), 1000);
    return () => clearTimeout(id);
  }, [timeLeft, submitted, endsAt]);

  const handleSubmit = useCallback(
    (isAuto = false) => {
      if (submitted) return;
      setSubmitted(true);
      setUserAnswers({ ...inputs }, isAuto ? 0 : timeLeft);
    },
    [submitted, inputs, timeLeft, setUserAnswers]
  );

  const timerColor =
    timeLeft > 15 ? '#15803d' : timeLeft > 8 ? '#f59e0b' : '#dc2626';

  const inputRefs = useRef({});

  const firstCategory = categories[0];
  useEffect(() => {
    if (finePointer && window.matchMedia('(min-width: 1024px)').matches) {
      inputRefs.current[firstCategory]?.focus();
    }
  }, [firstCategory]);

  const handleBoxKeyDown = (e, index) => {
    if (e.key !== 'Enter' || submitted || !window.matchMedia('(min-width: 1024px)').matches) return;
    e.preventDefault();
    const next = categories[index + 1];
    if (next) inputRefs.current[next]?.focus();
    else handleSubmit(false);
  };

  const opponent = botName && (
    <div className="flex items-center gap-3 rounded-xl border border-gray-100 bg-gray-50 px-3 py-2">
      <BotMascot bot={selectedBot} state={botReady ? 'won' : 'thinking'} size={40} />
      <p className="text-sm text-gray-600">
        <span className="font-bold text-gray-800">{botName}</span>{' '}
        {botReady ? <span className="text-green-600 font-medium">submitted. Hurry up!</span> : 'is typing…'}
      </p>
    </div>
  );

  const tracker = gameMode === 'full' ? (
    <div className="flex flex-wrap gap-1 justify-center">
      {ALL_LETTERS.map((letter) => {
        const isCurrent = letter === currentLetter;
        const isPlayed = playedLetters.includes(letter) && !isCurrent;
        return (
          <div
            key={letter}
            className={`w-9 h-9 rounded-lg flex items-center justify-center text-xs font-semibold border ${
              isCurrent
                ? 'bg-green-100 border-green-600 text-green-700 border-2'
                : isPlayed
                ? 'bg-gray-100 border-gray-300 text-gray-400'
                : 'bg-red-100 border-red-300 text-red-400'
            }`}
          >
            {letter}
          </div>
        );
      })}
    </div>
  ) : (
    <div className="flex justify-center items-center gap-2">
      {Array.from({ length: totalRounds }, (_, i) => {
        const roundNum = i + 1;
        const isDone    = roundNum < currentRound;
        const isCurrent = roundNum === currentRound;
        return (
          <div
            key={roundNum}
            className={`flex items-center justify-center rounded-full font-bold text-xs transition-all ${
              isCurrent
                ? 'w-9 h-9 bg-red-600 text-white shadow'
                : isDone
                ? 'w-7 h-7 bg-green-100 border-2 border-green-500 text-green-700'
                : 'w-7 h-7 bg-gray-100 border border-gray-300 text-gray-400'
            }`}
          >
            {isDone ? '✓' : roundNum}
          </div>
        );
      })}
    </div>
  );

  return (
    <div className="min-h-screen bg-orange-50 flex flex-col">
      {confirmDialog}
      <div className="flex-1 flex flex-col w-full lg:max-w-6xl lg:mx-auto lg:px-4">
      {/* Header */}
      <div className="px-5 pt-5 pb-3 flex justify-between items-center lg:px-0">
        <div className="flex items-center gap-3">
          <button
            onClick={handleQuit}
            className="-ml-3 min-h-[44px] px-3 rounded-lg text-sm text-gray-600 hover:text-red-600 hover:bg-red-50 transition-colors font-medium"
          >
            ✕ Quit
          </button>
          <p className="text-xs text-gray-600 font-medium">
            Round {currentRound} of {totalRounds}
          </p>
        </div>
      </div>

      <div className="px-4 mb-4 lg:hidden">{tracker}</div>

      <div className="flex-1 flex flex-col lg:flex-none lg:grid lg:grid-cols-[minmax(0,1fr)_320px] lg:gap-6 lg:items-start">
      {/* Game card */}
      <PeekingMascot
        mood={roundMood({ secondsLeft: timeLeft, totalSeconds: TOTAL_SECONDS, submitted })}
        className="flex-1 mx-4 mb-4 flex flex-col lg:mx-0 lg:mb-0"
      >
        <div className="bg-white rounded-2xl shadow-md p-5 h-full flex flex-col lg:p-8">
          {/* Letter + Timer */}
          <div className="flex justify-between items-center mb-6 lg:hidden">
            <span className="text-6xl font-bold text-red-600">{currentLetter}</span>
            <div className="text-right">
              <p
                className="text-4xl font-black tabular-nums"
                style={{ color: timerColor }}
              >
                {timeLeft}
              </p>
              <p className="text-xs text-gray-600">seconds</p>
            </div>
          </div>

          {/* Inputs */}
          <div className="flex-1 space-y-3 lg:flex-none lg:space-y-0 lg:grid lg:grid-cols-2 lg:gap-x-6 lg:gap-y-6">
            {categories.map((category, index) => (
              <div key={category}>
                <p className="text-sm font-semibold text-gray-700 capitalize mb-1.5 lg:text-base">
                  {CATEGORY_LABELS[category] || category}
                </p>
                <input
                  type="text"
                  ref={(el) => { inputRefs.current[category] = el; }}
                  onKeyDown={(e) => handleBoxKeyDown(e, index)}
                  value={inputs[category]}
                  onChange={(e) =>
                    setInputs((prev) => ({ ...prev, [category]: e.target.value }))
                  }
                  disabled={submitted}
                  placeholder={`${CATEGORY_LABELS[category]} starting with ${currentLetter}…`}
                  maxLength={40}
                  autoComplete="off"
                  autoCorrect="off"
                  spellCheck={false}
                  className={`w-full border-2 rounded-xl px-4 py-3 text-base lg:py-4 lg:text-lg text-gray-800 outline-none transition-colors placeholder:text-gray-300 ${
                    submitted
                      ? 'bg-gray-50 border-gray-200 text-gray-400'
                      : 'border-red-200 focus:border-red-500 bg-white'
                  }`}
                />
              </div>
            ))}
          </div>

          {/* Opponent */}
          {opponent && <div className="mt-4 lg:hidden">{opponent}</div>}

          {/* Submit */}
          <button
            onClick={() => handleSubmit(false)}
            disabled={submitted}
            className={`mt-5 w-full py-4 rounded-xl font-bold text-lg text-white lg:mt-8 ${
              submitted
                ? 'bg-gray-400 cursor-not-allowed'
                : 'btn-press bg-red-600 hover:bg-red-700'
            }`}
          >
            {submitted ? 'Submitted!' : 'Submit Answers'}
          </button>
          {finePointer && (
            <p className="hidden lg:block mt-3 text-center text-xs text-gray-500">
              <Kbd>Enter</Kbd> moves to the next box, and submits from the last one
            </p>
          )}
        </div>
      </PeekingMascot>

      <aside className="hidden lg:block lg:sticky lg:top-4 space-y-4">
        <div className="bg-white rounded-2xl shadow-md p-6 flex items-center justify-between">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Letter</p>
            <span className="text-7xl font-bold text-red-600 leading-none">{currentLetter}</span>
          </div>
          <div className="text-right">
            <p className="text-5xl font-black tabular-nums" style={{ color: timerColor }}>
              {timeLeft}
            </p>
            <p className="text-xs text-gray-600">seconds</p>
          </div>
        </div>
        {opponent && <div className="bg-white rounded-2xl shadow-md p-4">{opponent}</div>}
        <div className="bg-white rounded-2xl shadow-md p-5">
          <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-3 text-center">
            {gameMode === 'full' ? 'Letters played' : 'Rounds'}
          </p>
          {tracker}
        </div>
      </aside>
      </div>
      </div>
    </div>
  );
}
